import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';
import {
  Paper,
  Grid,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
} from '@mui/material';
import DataTable from 'components/utils/DataTable/';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import styles from '../Customers/styles.module.css';
import FeedbackService from 'services/FeedbackService';
import ShopSettingsService from 'services/ShopSettingsService';
import { ShopContext } from 'Context/ShopContext';
import StyledQRCode from './StyledQRCode';
import bottomLogoImage from '../../../images/bottom_logo.png';
import reviewHeaderImage from '../../../images/review_header.png';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import DateTimeHelpers from 'helpers/DateTimeHelpers';

function FeedbackManagement() {
  const { shop, setShop } = useContext(ShopContext);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [resettingToken, setResettingToken] = useState(false);
  const qrRef = useRef(null);
  const popupRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleDownloadPopup = async () => {
    if (!popupRef.current) return;
    try {
      const canvas = await html2canvas(popupRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const image = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = 'feedback-qr-poster.png';
      link.href = image;
      link.click();
    } catch (error) {
      console.error('Failed to capture popup', error);
    }
  };

  const fetchFeedbacks = useCallback(async () => {
    if (!shop?.id) return;
    setLoading(true);
    try {
      const data = await FeedbackService.getFeedbacks(shop.id);
      setFeedbacks(data);
    } catch (error) {
      console.error('Failed to fetch feedbacks (backend endpoint might not exist yet):', error);
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  }, [shop?.id]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  const headerData = [
    {
      label: 'Date & Time',
      id: 'created_at',
      type: 'callback',
      viewRender: (item) =>
        `${DateTimeHelpers.convertDateToIsoDMY(item.created_at)} ${DateTimeHelpers.convertTo12HourTime(
          item.created_at
        )}`,
    },
    { label: 'Customer', id: 'customer_name', type: 'text' },
    { label: 'Food', id: 'food_rating', type: 'text' },
    { label: 'Service', id: 'service_rating', type: 'text' },
    { label: 'Cleanliness', id: 'cleanliness', type: 'text' },
    { label: 'Overall', id: 'overall', type: 'text' },
    { label: 'Comment', id: 'comment', type: 'text' },
    {
      label: 'Status',
      id: 'status',
      type: 'callback',
      viewRender: (row) => (
        <Select
          value={row.status}
          onChange={(e) => {
            e.stopPropagation();
            handleStatusChange(row.id, e.target.value);
          }}
          size="small"
          sx={{
            minWidth: '120px',
            color: row.status === 'Resolved' ? '#4caf50' : row.status === 'Reviewed' ? '#ff851b' : '#2196f3',
            fontWeight: 600,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: row.status === 'Resolved' ? '#4caf50' : row.status === 'Reviewed' ? '#ff851b' : '#2196f3',
            },
            '& .MuiSelect-icon': {
              color: row.status === 'Resolved' ? '#4caf50' : row.status === 'Reviewed' ? '#ff851b' : '#2196f3',
            },
          }}
        >
          <MenuItem value="New" sx={{ color: '#2196f3', fontWeight: 600 }}>
            New
          </MenuItem>
          <MenuItem value="Reviewed" sx={{ color: '#ff851b', fontWeight: 600 }}>
            Reviewed
          </MenuItem>
          <MenuItem value="Resolved" sx={{ color: '#4caf50', fontWeight: 600 }}>
            Resolved
          </MenuItem>
        </Select>
      ),
    },
  ];

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await FeedbackService.updateFeedbackStatus(feedbackId, { status: newStatus });
      fetchFeedbacks();
    } catch (error) {
      console.error('Failed to update status', error);
    }
  };

  const handleOpenQr = () => setQrDialogOpen(true);
  const handleCloseQr = () => setQrDialogOpen(false);

  const handleResetToken = async () => {
    setResettingToken(true);
    try {
      await ShopSettingsService.regenerateFeedbackToken();
      const freshShopProfile = await ShopSettingsService.clearAndReloadShopProfile(shop.id);
      if (setShop) {
        setShop(freshShopProfile);
      }
    } catch (e) {
      console.error('Failed to reset token', e);
    } finally {
      setResettingToken(false);
    }
  };

  if (!shop?.id) return <div>No shop</div>;

  // Use the token provided by the backend in the shop config
  const secureToken = shop.feedback_token || '';
  const feedbackUrl = `${window.location.origin}/${shop.id}/feedback?token=${secureToken}`;

  return (
    <div className={styles.contentWrapper}>
      <div className={`${styles.titleSec} no-print`}>
        <h2 className={styles.title}>
          Feedback
          <span className={styles.menuTitle}>Management</span>
        </h2>
        <div className="buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>&nbsp;</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#9c27b0' }}
              onClick={fetchFeedbacks}
              disabled={loading}
            >
              <RefreshIcon className={styles.actionBtnIcon} style={{ marginRight: '8px' }} />
              RELOAD
            </Button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>Collect Feedback</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#ff851b' }}
              onClick={handleOpenQr}
            >
              <QrCodeScannerIcon className={styles.actionBtnIcon} style={{ marginRight: '8px' }} />
              {secureToken ? 'Show QR Code' : 'Generate QR Code'}
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.changeable}>
        <Grid container>
          <Grid item xs={12} sm={4} className={styles.headTitle}>
            <h2 className={styles.subTitle}>All Feedbacks</h2>
          </Grid>
        </Grid>
      </div>

      <Paper className={styles.tableWrapper}>
        <DataTable columns={headerData} rows={feedbacks} isLoading={loading} deactiveRow />
      </Paper>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={handleCloseQr}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, position: 'relative' } }}
      >
        <IconButton
          aria-label="close"
          onClick={handleCloseQr}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#777',
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
          <Box
            ref={popupRef}
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={4}
            px={0}
            bgcolor="#ffffff"
            sx={{ width: '100%', boxSizing: 'border-box' }}
          >
            <style>
              {`
                @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Montserrat:wght@400;500;600&display=swap');
              `}
            </style>

            <Box textAlign="center" mb={3} mt={2} sx={{ width: '100%', padding: 0 }}>
              <img
                src={reviewHeaderImage}
                alt="Share the love with a review"
                style={{ width: '100%', display: 'block', margin: '0 auto' }}
              />
            </Box>

            {secureToken ? (
              <>
                <Box p={2} bgcolor="#fff" borderRadius={4} style={{ display: 'inline-block' }}>
                  <StyledQRCode ref={qrRef} url={feedbackUrl} logo={shop?.shop_logo} />
                </Box>

                <Typography
                  variant="body1"
                  align="center"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    marginTop: '30px',
                    color: '#333',
                    maxWidth: '85%',
                    lineHeight: 1.5,
                    fontSize: '14px',
                  }}
                >
                  We value your feedback, we would appreciate if you could take a moment to leave us a review
                </Typography>
              </>
            ) : (
              <Box py={4} display="flex" flexDirection="column" alignItems="center">
                <Typography
                  variant="body1"
                  align="center"
                  style={{ fontFamily: "'Montserrat', sans-serif", marginBottom: '24px', color: '#555' }}
                >
                  No feedback QR code has been generated for this shop yet.
                </Typography>
                <Button
                  onClick={handleResetToken}
                  disabled={resettingToken}
                  variant="contained"
                  color="primary"
                  style={{
                    backgroundColor: '#ff851b',
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 600,
                    padding: '10px 24px',
                    borderRadius: 8,
                  }}
                >
                  {resettingToken ? 'Generating...' : 'Generate QR Code'}
                </Button>
              </Box>
            )}

            <Box display="flex" alignItems="center" justifyContent="center" mt={6} mb={1}>
              <img src={bottomLogoImage} alt="Powered by Sanoft" style={{ width: '200px' }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between', padding: '16px 24px', borderTop: '1px solid #eee' }}>
          {secureToken ? (
            <IconButton onClick={handleResetToken} color="secondary" disabled={resettingToken} title="Reset QR Code">
              <RefreshIcon />
            </IconButton>
          ) : (
            <div />
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            {secureToken && (
              <>
                <Button
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(feedbackUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  color="primary"
                  variant="outlined"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 600,
                    borderRadius: 20,
                    textTransform: 'none',
                    padding: '4px 16px',
                    fontSize: '0.8rem',
                  }}
                >
                  {copied ? 'Copied!' : 'Copy URL'}
                </Button>
                <Button
                  onClick={handleDownloadPopup}
                  color="primary"
                  variant="contained"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 600,
                    borderRadius: 20,
                    textTransform: 'none',
                    padding: '6px 24px',
                    backgroundColor: '#111',
                  }}
                >
                  Download
                </Button>
              </>
            )}
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default withConsoleBase(FeedbackManagement);
